import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { checkDunning, dunningStageLabel, findCreatorInvoiceDuplicates, sortByUrgency } from "@talentkontor/domain";
import { api } from "../../../data";
import { EVENT_LABEL, loadInvoiceDetail, type InvoiceEventRow, type PayoutInfo } from "./detail-data";
import { invoicePdfForDownload, saveBlob } from "./beleg";
import { withStoredExtension } from "../../../model/e-rechnung";
import { invoiceListCsv, zipPaths, type ExportRow } from "./liste-export";
import { zipFiles } from "../../creator/zip";
import { toast } from "../../../components/Toast";
import { BeispielEtikett } from "../../../components/Beispiel";
import "./rechnungen.css";
import { useStaffWorkspace } from "../../../app/session";
import { talentName, useBrands, useDeals, useInvoices, useOrgSettings, usePeriods, useTalents } from "../../../data/queries";
import { ErrorState, Loading } from "../../../components/States";
import { Dialog, KopfMehr, Money, PageHead, SortTh, Toolbar, matchesSearch, useAction, useSorted, SortWahl } from "../../../components/ui";
import { date, money, monthLabel, monthName, today as todayIn } from "../../../lib/format";
import { useOrgConfig } from "../../../model/org-config";
import { nurLesenSatz, useNurLesen, useNurLesenPause } from "../../../app/nurLesen";
import { actionLabel, RESPONSIBLE, stateContext, viewInvoice, type InvoiceView, type Tone, holidayRegionOf } from "../../../model/invoice-view";
import { balanceCents, invoiceFilterOf, isOverdueReceivable, mailFailedNote, negativerBeleg, type InvoiceFilter } from "../../../model/invoice-activity";
import { handlungsbedarf, kundeZeile, letzteAktivitaet, listFilterMatches, mahnstufePille, naechsterSchritt, statusZeile, type ListFilter, type StatusZeile } from "../../../model/rechnungsliste";
import { currentLang, t, ta, uiLocale } from "../../../lib/sprache";
import { VoreinstellungenEintrag } from "../einstellungen/Voreinstellungen";
import { Mehr } from "../../../components/ZeilenMenue";
import { PdfVorschau, type PdfQuelle } from "../../../components/PdfVorschau";
import { zeilenAktionen, zeilenAktionText, type ZeilenAktion } from "./zeilen-aktionen";
import { PdfDesignLink } from "../../../components/PdfDesignLink";
import { MehrLaden, useSeitenweise } from "../../../components/MehrLaden";
import { useHandy } from "../../../lib/handy";
import { RechnungsKennzahlen } from "./Kennzahlen";
import { rechnungsKennzahlen } from "./kennzahlen-model";

/**
 * Rechnungen (11_app_agentur/rechnungen.html): Eingang und Ausgang in einer Liste, nach Dringlichkeit sortiert. Seit K-409 dichter
 * und mit mehr Angaben: Rechnung mit Versandzeichen, Kunde, Creator, Status als Farbpunkt mit Klartext, Mahnstufe als Pille,
 * letzte Aktivität mit Datum, Fälligkeit, Betrag und Saldo, wer am Zug ist, nächster Schritt mit „Mehr“ (rechts fixiert).
 * Schmalere Fenster blenden Nebenspalten in die Hauptspalten ein (Fällig und Betrag bleiben immer stehen); unter 768 px Karten.
 * Mehrfachauswahl für CSV, ZIP und „Erinnerung an alle fälligen“ (eine Rechnung nach der anderen, jede einzeln bestätigt).
 */

const FILTERS: { key: ListFilter; label: string }[] = [
  { key: "alle", get label() { return t("Alle", "All"); } },
  { key: "handlung", get label() { return t("Handlungsbedarf", "Action needed"); } },
  { key: "ueberfaellig", get label() { return t("Überfällig", "Overdue"); } },
  { key: "offen", get label() { return t("Offen", "Open"); } },
  { key: "pruefung", get label() { return t("In Prüfung", "In review"); } },
  { key: "entwurf", get label() { return t("Entwürfe", "Drafts"); } },
  { key: "bezahlt", get label() { return t("Bezahlt", "Paid"); } },
  { key: "storniert", get label() { return t("Storniert", "Cancelled"); } },
];

type Row = {
  v: InvoiceView;
  /** Kunde im rechtlichen Sinn (Export): bei Creator-Rechnungen die Agentur */
  customer: string;
  subject: string;
  /** R-01: Hauptzeile der Liste: bei Creator-Rechnungen die Marke bzw. der Deal statt der eigenen Agentur */
  kunde: string;
  betreff: string;
  talent: string;
  brand: string;
  /** K-561 a: Marke der Rechnung, sonst die der Kooperation (Creator-Rechnungen tragen keine eigene) */
  brandId: string | null;
  deal: string;
  responsible: string;
  balance: number;
  status: StatusZeile;
  stufe: { label: string; tone: Tone } | null;
  aktivitaet: { label: string; on: string | null };
  /** Kann jetzt gemahnt werden (Erinnerung bzw. nächste Stufe fällig) */
  mahnbar: boolean;
  /** Andere wirksame Creator-Rechnungen zur selben Kooperation und zum selben Monat (AD-134, RE-40) */
  duplicates: string[];
  /** 0180: gescheiterte Mail an die Marke (Mahnung, Storno, Rechnungsmail) als kurzer Hinweis, sonst null */
  mailFailed: string | null;
  /** K-542: Schritte der Detailseite für diesen Stand (Zeilenmenü) */
  aktionen: ZeilenAktion[];
};

/** K-428: Stufen der eingeklappten Spalten (1 bis 4), Stufe 5 zusätzlich ohne Fixierung der letzten Spalte */
const ENG_MAX = 5;

/** Kurzes Datum („25.09.“ bzw. „25/09“) für Nebenzeilen */
const kurzDatum = (iso: string) => new Intl.DateTimeFormat(uiLocale(), { day: "2-digit", month: "2-digit", timeZone: "UTC" }).format(new Date(`${iso.slice(0, 10)}T12:00:00Z`));

function Versandzeichen({ sentOn, deliveredOn, failed }: { sentOn: string | null; deliveredOn: string | null; failed: boolean }) {
  if (!sentOn && !failed) return null;
  const titel = failed ? t("Zustellung gescheitert", "Delivery failed") : deliveredOn ? t(`Zugegangen am ${date(deliveredOn)}`, `Delivered on ${date(deliveredOn)}`) : t(`Versendet am ${date(sentOn)}`, `Sent on ${date(sentOn)}`);
  return (
    <span className={`tk-inv-versand${failed ? " tk-inv-versand-fehler" : deliveredOn ? " tk-inv-versand-zu" : ""}`} title={titel} role="img" aria-label={titel}>
      <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true"><path d="M4 6h16v12H4z M4 7l8 6 8-6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        {deliveredOn && !failed && <path d="M15 17.5l2 2 4-4.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />}</svg>
    </span>
  );
}


export function Rechnungen() {
  const ws = useStaffWorkspace();
  const navigate = useNavigate();
  // 0425: Pause und Nur-Lesen: Liste lesen, aber keine Schritte anbieten
  const nurLesen = useNurLesen();
  const nurLesenPause = useNurLesenPause();
  const orgId = ws.org.org_id;
  const q = { cfg: useOrgConfig(orgId), settings: useOrgSettings(orgId), invoices: useInvoices(orgId), periods: usePeriods(orgId), deals: useDeals(orgId), talents: useTalents(orgId), brands: useBrands(orgId) };
  const [search, setSearch] = useState("");
  // Einstieg mit Filter aus Links der Übersicht (z. B. ?filter=offen)
  const [params] = useSearchParams();
  const [filter, setFilterState] = useState<ListFilter>(() => FILTERS.find((f) => f.key === params.get("filter"))?.key ?? "alle");
  // Aus der Kachel „Davon überfällig“: nur überfällige Forderungen, dieselbe Menge wie dort (SICHT-AGENTUR-GELD-22)
  const [nurForderungen, setNurForderungen] = useState(() => params.get("filter") === "ueberfaellig" && params.get("art") === "forderung");
  const setFilter = (f: ListFilter) => { setNurForderungen(false); setFilterState(f); };
  // K-523: nach Leistungsmonat, Creator oder Marke gruppieren (mit Zwischensummen) und auf einen Leistungsmonat filtern
  const [gruppe, setGruppe] = useState<"" | "monat" | "creator" | "marke">("");
  const [monatFilter, setMonatFilter] = useState("");
  const [zugeklappt, setZugeklappt] = useState<Set<string>>(() => new Set());
  const [auswahl, setAuswahl] = useState<Set<string>>(() => new Set());
  // K-542: Vorschau des Belegs direkt aus dem Zeilenmenü (wie K-538 auf der Detailseite)
  const [vorschau, setVorschau] = useState<PdfQuelle | null>(null);
  // K-518: Schnellansicht einer Rechnung, ohne die Liste zu verlassen
  const [schnell, setSchnell] = useState<Row | null>(null);
  const today = todayIn(ws.org.timezone);
  // Fälligkeit der Auszahlungen (Auszahlungsmodus, Zahlung der Marke auch über Sammelrechnungen, 0055)
  const incomingIds = (q.invoices.data ?? []).filter((i) => i.kind === "talent_to_agency" && ["approved", "sent"].includes(i.status)).map((i) => i.id).sort();
  const payoutInfo = useQuery({ queryKey: ["invoice_payout_info", orgId, incomingIds.join(",")], enabled: incomingIds.length > 0,
    queryFn: () => api.read<PayoutInfo[]>("invoice_payout_info", { p_invoices: incomingIds }).catch(() => [] as PayoutInfo[]) });
  // Gesamtnachtest (03.11.2026), A: Auszahlungskachel aus derselben Abfrage wie die Seite Auszahlungen (gleicher Schlüssel)
  const auszahlungsKandidaten = useQuery({ queryKey: ["payout_candidates", orgId], enabled: ws.can("trigger_payouts"),
    queryFn: () => api.read<{ amount_cents: number; currency: string }[]>("payout_candidates", { p_org: orgId }).catch(() => null) });
  const list = Object.values(q);
  const error = list.find((x) => x.error)?.error;

  const rows = useMemo<Row[]>(() => {
    if (!q.cfg.data || !q.invoices.data || !q.periods.data || !q.deals.data || !q.talents.data || !q.brands.data) return [];
    const payoutDue = new Map((payoutInfo.data ?? []).map((p) => [p.invoice_id, p.payout_due ?? null]));
    const ctxBase = { cfg: q.cfg.data, today, holidayRegion: holidayRegionOf(q.settings.data), periods: new Map(q.periods.data.map((p) => [p.id, p])), payoutDue };
    const views = q.invoices.data.map((r) => viewInvoice(r, ctxBase)).filter((x): x is InvoiceView => !!x);
    const sctx = stateContext(ctxBase);
    const ordered = sortByUrgency(views.map((v) => v.record), sctx);
    const byId = new Map(views.map((v) => [v.row.id, v]));
    const deals = new Map(q.deals.data.map((d) => [d.id, d]));
    const talents = new Map(q.talents.data.map((t) => [t.id, t]));
    const brands = new Map(q.brands.data.map((b) => [b.id, b]));
    const policy = q.cfg.data.dunning;
    // Dublettenwarnung (0161): zweite wirksame Creator-Rechnung zur selben Kooperation und zum selben Kooperationsmonat
    const periodMonth = new Map(q.periods.data.map((p) => [p.id, p.month]));
    const dups = findCreatorInvoiceDuplicates(q.invoices.data.map((i) => ({ id: i.id, kind: i.kind, dealId: i.deal_id, status: i.status, creditFor: i.credit_for ?? null,
      month: i.period_id ? periodMonth.get(i.period_id) ?? i.revenue_month : null })));
    const numberOf = new Map(q.invoices.data.map((i) => [i.id, i.number ?? t("ohne Nummer", "no number")]));
    return ordered.map((rec) => {
      const v = byId.get(rec.id)!;
      const d = v.row.deal_id ? deals.get(v.row.deal_id) : undefined;
      const month = v.row.revenue_month ? monthName(v.row.revenue_month) : "";
      const brandId = v.row.brand_id ?? d?.brand_id ?? null;
      const brand = brands.get(brandId ?? "")?.name ?? "";
      const incoming = v.row.kind === "talent_to_agency";
      const toCreator = v.row.kind === "agency_to_talent";
      const creator = talentName(v.row.talent_id ? talents.get(v.row.talent_id) : undefined);
      // Name der nächsten Stufe nur für überfällige Forderungen (Nebenzeile „1. Mahnung fällig in 4 T“)
      let nextStage: string | null = null;
      let mahnbar = false;
      // K-409: negative Belege (Storno, Gutschrift) werden nie gemahnt
      if (!incoming && !negativerBeleg(v) && (v.state.state === "overdue" || v.state.state === "dunning" || v.state.state === "due_today")) {
        const c = checkDunning(v.record, sctx);
        nextStage = c.nextStage ? (c.nextStage.isReminder ? t("Erinnerung", "Reminder") : dunningStageLabel(policy, c.nextStage.key, currentLang())) : null;
        mahnbar = v.next?.action === "send_dunning";
      }
      return {
        v,
        // Kunde: bei Eingangsrechnungen die Agentur, bei Rechnungen an den Creator der Creator, sonst die Marke
        customer: incoming ? ws.org.name : toCreator ? creator : brand || "–",
        subject: [month, incoming ? brand : d?.title].filter(Boolean).join(" · "),
        ...kundeZeile(v.row.kind, { agentur: ws.org.name, creator, marke: brand, deal: d?.title ?? "", monat: month }),
        talent: creator,
        brand, brandId,
        deal: d?.title ?? "",
        responsible: RESPONSIBLE[v.state.responsible] ?? "–",
        balance: balanceCents(v),
        status: statusZeile(v, today, nextStage),
        stufe: incoming ? null : mahnstufePille(v, policy),
        aktivitaet: letzteAktivitaet(v, policy),
        mahnbar,
        duplicates: (dups.get(v.row.id) ?? []).map((id) => numberOf.get(id) ?? ""),
        mailFailed: mailFailedNote(v),
        aktionen: zeilenAktionen(v, ws, sctx),
      };
    });
  }, [q.cfg.data, q.invoices.data, q.periods.data, q.deals.data, q.talents.data, q.brands.data, q.settings.data, today, ws, payoutInfo.data]);

  const counts = useMemo(() => {
    const c: Record<ListFilter, number> = { alle: 0, handlung: 0, entwurf: 0, pruefung: 0, offen: 0, ueberfaellig: 0, bezahlt: 0, storniert: 0 };
    for (const r of rows) {
      c[invoiceFilterOf(r.v)] += 1;
      if (handlungsbedarf(r.v)) c.handlung += 1;
      c.alle += 1;   // „Alle“ zeigt alle, auch bezahlte und stornierte (Praxistest K-044)
    }
    return c;
  }, [rows]);

  const leistungsmonat = (r: Row) => (r.v.row.revenue_month ?? r.v.row.issue_date ?? "").slice(0, 7);
  const monate = useMemo(() => [...new Set(rows.map(leistungsmonat).filter(Boolean))].sort().reverse(), [rows]);
  const shown = rows.filter((r) => {
    if (monatFilter && leistungsmonat(r) !== monatFilter) return false;
    const inFilter = listFilterMatches(r.v, filter);
    if (inFilter && nurForderungen && filter === "ueberfaellig" && !isOverdueReceivable(r.v)) return false;
    return inFilter && matchesSearch(`${r.v.row.number ?? ""} ${r.customer} ${r.subject} ${r.kunde} ${r.betreff} ${r.talent} ${r.v.pill.label} ${r.status.text} ${r.stufe?.label ?? ""} ${r.mailFailed ?? ""}${r.v.row.imported ? t(" übernommen", " taken over") : ""}`, search);
  });
  const { sorted, sort, toggle } = useSorted(shown, {
    nummer: (r) => r.v.row.issue_date,
    kunde: (r) => r.kunde,
    talent: (r) => r.talent,
    status: (r) => r.status.text,
    stufe: (r) => r.stufe?.label ?? null,
    aktivitaet: (r) => r.aktivitaet.on,
    faellig: (r) => r.v.row.effective_due_date,
    betrag: (r) => r.v.row.gross_cents,
    saldo: (r) => r.balance,
    zustaendig: (r) => r.responsible,
    schritt: (r) => actionLabel(r.v.next, r.v.row.kind),
  });
  // R-02 (Umbauten 03.11.2026): seitenweise statt alle Zeilen auf einmal (am Handy 20, sonst 50 je Portion); Filter, Suche,
  // Sortierung und Gruppierung gelten weiter für die ganze Liste, der Wechsel beginnt wieder bei der ersten Portion
  const handy = useHandy();
  const seite = useSeitenweise(sorted, handy ? 20 : 50, [filter, nurForderungen, search, monatFilter, gruppe, sort.key, sort.dir].join("|"));

  // Kennzahlen je Währung über alle Rechnungen: offene Forderungen (an Marken und Creator), Zahl der offenen, Handlungsbedarf;
  // offene Auszahlungen an Creator stehen getrennt dahinter. Prüf-Chat Nachtest (03.11.2026): Agenturwährung immer zuerst.
  const kennzahlen = rows.length ? rechnungsKennzahlen(rows, ws.org.currency, auszahlungsKandidaten.data ?? null) : [];
  // Antwort eines Creators (Zahlung gemeldet oder „Geld fehlt“): Hinweis über der Liste
  const antworten = rows.filter((r) => r.v.row.kind !== "talent_to_agency" && (r.v.state.state === "money_missing" || r.v.state.state === "payment_reported"));

  // K-428: Die rechts fixierte Spalte darf den Betrag nie verdecken. Ist die Tabelle breiter als ihr Rahmen (lange Beträge wie
  // „EUR 3'456.00“, lange Namen), klappt die Liste Stufe um Stufe weitere Nebenspalten ein (wie die Containerabfragen) und gibt
  // zuletzt die Fixierung auf; gemessen vor dem Zeichnen, neu bei anderer Breite, Ansicht oder Suche.
  const tabelle = useRef<HTMLDivElement>(null);
  // Prüf-Chat Nachtest (03.11.2026), R-11: „Letzte Aktivität“ steht nie als leere Spalte (0 px) im DOM. Blendet die Breite sie aus
  // (Containerabfrage oder Stufe), fällt sie weg und ihr Inhalt steht als Nebenzeile im Status (data-ohne-akt); wird die Tabelle
  // wieder breiter als dort, kommt sie zurück und wird neu gemessen.
  const [aktSpalte, setAktSpalte] = useState(true);
  const aktWegBei = useRef<number | null>(null);
  // direkt am Element (data-eng), damit React die Stufe beim nächsten Zeichnen nicht überschreibt; gemessen vor dem Malen
  const passe = () => {
    const w = tabelle.current;
    if (!w) return;
    w.removeAttribute("data-eng");
    if (typeof window === "undefined" || window.matchMedia?.("(max-width: 767px)").matches) {
      // Karten am Handy zeigen die Aktivität als eigene Zeile
      if (!aktSpalte) { aktWegBei.current = null; setAktSpalte(true); }
      return;
    }
    for (let n = 1; n <= ENG_MAX && w.scrollWidth > w.clientWidth + 1; n++) w.setAttribute("data-eng", String(n));
    const th = w.querySelector<HTMLElement>("th.tk-c-akt");
    if (th && getComputedStyle(th).display === "none") { aktWegBei.current = w.clientWidth; setAktSpalte(false); }
  };
  useLayoutEffect(passe);
  useEffect(() => {
    const w = tabelle.current;
    if (!w || typeof ResizeObserver === "undefined") return;
    let breite = -1;
    const beobachter = new ResizeObserver(() => {
      if (Math.abs(w.clientWidth - breite) <= 1) return;
      breite = w.clientWidth;
      if (!aktSpalte && aktWegBei.current !== null && w.clientWidth > aktWegBei.current + 1) { aktWegBei.current = null; setAktSpalte(true); return; }
      passe();
    });
    beobachter.observe(w);
    return () => beobachter.disconnect();
  });
  const { busy, run } = useAction();
  if (error) return <ErrorState error={error} retry={() => list.forEach((x) => x.error && void x.refetch())} />;
  if (list.some((x) => x.isPending)) return <Loading />;

  const dueOf = (r: Row): string => {
    if (r.v.row.kind === "talent_to_agency" && r.v.record.payoutDueDate !== undefined) return r.v.record.payoutDueDate ? date(r.v.record.payoutDueDate) : t("nach Zahlung der Marke", "after the brand's payment");
    if (["delivery_pending", "delivery_failed"].includes(r.v.state.state)) return t("ab Zugang", "from receipt");
    return date(r.v.row.effective_due_date);
  };
  // Export (PARITAET-6): genau die Zeilen aus Filter, Suche und Sortierung, bei einer Auswahl nur die ausgewählten
  const gewaehlt = sorted.filter((r) => auswahl.has(r.v.row.id));
  const exportRows = (rs: readonly Row[]): ExportRow[] => rs.map((r) => ({
    number: r.v.row.number, kind: r.v.row.kind, issueDate: r.v.row.issue_date, revenueMonth: r.v.row.revenue_month, customer: r.customer, brand: r.brand,
    creator: r.talent === "–" ? "" : r.talent, deal: r.deal, status: r.v.pill.label, due: dueOf(r), currency: r.v.row.currency, netCents: r.v.row.net_cents,
    taxCents: r.v.row.tax_cents, grossCents: r.v.row.gross_cents, openCents: r.balance, selfBilled: !!r.v.row.self_billed, creditNote: !!r.v.row.credit_for,
  }));
  const label = FILTERS.find((f) => f.key === filter)?.label ?? t("Alle", "All");
  const stamp = (rs: readonly Row[]) => `${today}_${rs === sorted ? label.replace(/\s+/g, "-") : t("Auswahl", "Selection")}`;
  const exportCsv = (rs: readonly Row[]) => saveBlob(new Blob([invoiceListCsv(exportRows(rs), ws.can("view_amounts"))], { type: "text/csv;charset=utf-8" }), `Rechnungen_${stamp(rs)}.csv`);
  const exportZip = (rs: readonly Row[]) => void run("zip", async () => {
    const paths = zipPaths(exportRows(rs));
    const files: { name: string; data: Uint8Array }[] = [];
    let missing = 0;
    for (const [k, r] of rs.entries()) {
      const path = (r.v.row.pdf_path as string | null) ?? null;
      let blob = path ? await api.download("documents", path).catch(() => null) : null;
      // Im Portal geschriebene Belege ohne Archiv: aus den eingefrorenen Angaben (wie „Rechnung als PDF herunterladen“)
      if (!blob && r.v.row.status !== "draft" && !r.v.row.imported && (r.v.row.kind === "agency_to_brand" || r.v.row.kind === "agency_to_talent" || r.v.row.self_billed)) {
        const d = await loadInvoiceDetail(orgId, r.v.row.id).catch(() => null);
        blob = d ? (await invoicePdfForDownload(d, ws.org.name).catch(() => null))?.blob ?? null : null;
      }
      if (!blob) { missing += 1; continue; }
      files.push({ name: withStoredExtension(paths[k]!, path), data: new Uint8Array(await blob.arrayBuffer()) });
    }
    if (!files.length) throw new Error(t("Zu den Rechnungen dieser Ansicht liegt noch kein PDF vor.", "There is no PDF yet for the invoices in this view."));
    saveBlob(new Blob([zipFiles(files, { folders: true }) as BlobPart], { type: "application/zip" }), `Rechnungen_${stamp(rs)}.zip`);
    toast(t(`${files.length} ${files.length === 1 ? "PDF" : "PDFs"} als ZIP gespeichert${missing ? t(`; ${missing} ohne PDF (Entwurf oder Original fehlt)`, `; ${missing} without PDF (draft or original missing)`) : ""}.`, `${files.length} ${files.length === 1 ? "PDF" : "PDFs"} saved as ZIP${missing ? t(`; ${missing} ohne PDF (Entwurf oder Original fehlt)`, `; ${missing} without PDF (draft or original missing)`) : ""}.`));
  });
  const belegLaden = async (r: Row) => {
    const d = await loadInvoiceDetail(orgId, r.v.row.id);
    if (!d) throw new Error(t("Die Rechnung ließ sich nicht laden.", "The invoice could not be loaded."));
    return invoicePdfForDownload(d, ws.org.name);
  };
  const pdfLaden = (r: Row) => void run(`pdf:${r.v.row.id}`, async () => {
    const f = await belegLaden(r);
    saveBlob(f.blob, f.filename);
  });

  const erinnernHref = (id: string, rest: string[] = []) => `/agentur/rechnungen/${id}/erinnern?${new URLSearchParams(rest.length ? { von: "liste", folge: rest.join(",") } : { von: "liste" })}`;
  const nextHref = (r: Row) => {
    const a = r.v.next?.action;
    if (a === "send_dunning" || a === "set_dunning_stage") return erinnernHref(r.v.row.id);
    return `/agentur/rechnungen/${r.v.row.id}`;
  };
  // „Erinnerung an alle fälligen“: die ausgewählten, jetzt mahnbaren Rechnungen nacheinander im Versandfenster
  const darfMahnen = ws.can("dun");
  const faelligeAuswahl = gewaehlt.filter((r) => r.mahnbar);
  const alleErinnern = () => {
    const ids = faelligeAuswahl.map((r) => r.v.row.id);
    if (ids.length) navigate(erinnernHref(ids[0]!, ids.slice(1)));
  };
  const alleGewaehlt = sorted.length > 0 && sorted.every((r) => auswahl.has(r.v.row.id));
  const setAlle = (an: boolean) => setAuswahl(an ? new Set([...auswahl, ...sorted.map((r) => r.v.row.id)]) : new Set([...auswahl].filter((id) => !sorted.some((r) => r.v.row.id === id))));
  const umschalten = (id: string) => setAuswahl((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const sichtbar = ws.can("view_amounts");

  // K-523: eine Zeile der Liste (auch unter Gruppenköpfen)
  const zeileVon = (r: Row) => {
              const due = dueOf(r);
              const dueDate = r.v.row.effective_due_date;
              const overdue = r.v.row.kind !== "talent_to_agency" && isOverdueReceivable(r.v);
              const schritt = naechsterSchritt(r.v, kurzDatum);
              const id = r.v.row.id;
              // R-01: Hauptzeile Kunde (bei Creator-Rechnungen die Marke), darunter Monat · Kooperation, schmaler mit dem Creator
              const sub = r.betreff;
              const creatorZeile = [sub, r.talent !== "–" && r.talent !== r.kunde ? r.talent : ""].filter(Boolean).join(" · ");
              const nr = r.v.row.number ?? t("Ohne Nummer", "Without number");
              const akt = r.aktivitaet.on ? `${r.aktivitaet.label} ${kurzDatum(r.aktivitaet.on)}` : r.aktivitaet.label;
              const zug = r.responsible === "–" ? null : ws.isCreator && r.responsible === t("Agentur", "Agency") ? t("du bist am Zug", "your turn") : t(`${r.responsible} am Zug`, `next: ${r.responsible.toLowerCase()}`);
              return (
                <tr key={id} aria-selected={auswahl.has(id)}>
                  <td className="tk-c-sel"><input type="checkbox" checked={auswahl.has(id)} onChange={() => umschalten(id)} aria-label={t(`${nr} auswählen`, `Select ${nr}`)} /></td>
                  <td className="tk-c-nr" data-label={t("Rechnung", "Invoice")}>
                    <span className="tk-invcell">
                      <strong className="tk-inv-zeile"><Link to={`/agentur/rechnungen/${id}`}>{nr}</Link><BeispielEtikett von={r.v.row} /></strong>
                      <small className="tk-inv-zeile" title={`${date(r.v.row.issue_date)}${r.v.row.self_billed ? t(" · Gutschrift", " · self-billing invoice") : r.v.row.credit_for ? t(" · Stornorechnung", " · cancellation invoice") : ""}${r.v.row.imported ? t(" · übernommen", " · taken over") : ""}`}>
                        {date(r.v.row.issue_date)}
                        <Versandzeichen sentOn={r.v.record.sentToBrandAt} deliveredOn={r.v.record.deliveredAt ?? null} failed={r.v.state.state === "delivery_failed"} />
                        {r.v.row.self_billed ? t(" · Gutschrift", " · self-billing invoice") : r.v.row.credit_for ? t(" · Stornorechnung", " · cancellation invoice") : ""}{r.v.row.imported ? t(" · übernommen", " · taken over") : ""}
                      </small>
                      {r.duplicates.length > 0 && (
                        <span className="pill warning" title={t(`Möglicherweise doppelt: ${r.duplicates.join(", ")} gehört zur selben Kooperation und zum selben Monat.`, `Possibly a duplicate: ${r.duplicates.join(", ")} belongs to the same deal and month.`)}>
                          {t(`! Möglicherweise doppelt (${r.duplicates.join(", ")})`, `! Possibly a duplicate (${r.duplicates.join(", ")})`)}
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="tk-c-kunde" data-label={t("Kunde", "Customer")}>
                    {/* Name und Betreff je eine Zeile, Rest mit „…“; ganz im Tooltip */}
                    <span className="tk-invcell">
                      {/* Kunde fett; darunter Betreff, schmaler mit Creator; ganz schmal (Spalte Rechnung eingeklappt) zuerst Nummer · Datum, dann Betreff · Creator (R-01: zweizeilig statt einer gekürzten Zeile) */}
                      <span className="tk-inv-zeile tk-inv-kunde" title={r.kunde}>{r.kunde}</span>
                      <small className="tk-inv-zeile tk-alt-nr" title={`${nr} · ${date(r.v.row.issue_date)}`}><Link to={`/agentur/rechnungen/${id}`}>{nr}</Link> · {date(r.v.row.issue_date)}</small>
                      {sub && <small className="tk-inv-zeile tk-ohne-creator" title={sub}>{sub}</small>}
                      {creatorZeile && <small className="tk-inv-zeile tk-alt-creator" title={creatorZeile}>{creatorZeile}</small>}
                    </span>
                  </td>
                  {/* Studio (Creator-Konto): keine Spalte „Creator“ mit dem eigenen Namen; der Deal steht unter Kunde / Betreff */}
                  {!ws.isCreator && <td className="tk-c-creator" data-label="Creator"><span className="tk-inv-zeile" title={r.talent}>{r.talent !== r.kunde ? r.talent : "–"}</span></td>}
                  <td className="tk-c-status" data-label="Status">
                    <span className="tk-invcell">
                      <span className={`tk-inv-status tk-ton-${r.status.tone}`}><span className="tk-inv-punkt" aria-hidden="true" />{r.status.text}</span>
                      {/* breit: Nebenzeile des Status; schmaler (Spalten Aktivität und Am Zug ausgeblendet): Nebenzeile oder letzte Aktivität, dazu wer am Zug ist */}
                      {(r.status.sub || zug) && <small className={`tk-inv-zeile tk-nur-breit${r.status.sub ? ` tk-ton-${r.status.tone === "error" ? "warning" : r.status.tone}` : ""}`} title={[r.status.sub, zug].filter(Boolean).join(" · ")}>{[r.status.sub, zug].filter(Boolean).join(" · ")}</small>}
                      <small className={`tk-inv-zeile tk-alt-akt${r.status.sub ? ` tk-ton-${r.status.tone === "error" ? "warning" : r.status.tone}` : ""}`} title={[r.status.sub ?? akt, zug].filter(Boolean).join(" · ")}>{[r.status.sub ?? akt, zug].filter(Boolean).join(" · ")}</small>
                      {/* R-01: in der engsten Stufe (1024 px) steht die Fälligkeit hier statt in einer eigenen Spalte */}
                      <small className={`tk-inv-zeile tk-umbau-alt-faellig${overdue ? " tk-ton-error" : ""}`} title={t(`Fällig ${due}`, `Due ${due}`)}>{t(`Fällig ${due}`, `Due ${due}`)}</small>
                      {/* D-2 (Inhaber 21.10.2026): Mahnstufe und Mahnsperre gehören zum Status, keine eigene Spalte mehr */}
                      {(r.stufe || r.v.row.dunning_blocked) && (
                        <span className="tk-inv-stufen">
                          {r.stufe && <span className={`pill ${r.stufe.tone}`}>{r.stufe.label}</span>}
                          {r.v.row.dunning_blocked && <span className="pill neutral">{t("Mahnsperre", "Dunning block")}</span>}
                        </span>
                      )}
                    </span>
                  </td>
                  {aktSpalte && (
                    <td className="tk-c-akt" data-label={t("Letzte Aktivität", "Last activity")}>
                      <span className="tk-invcell">
                        <span className="tk-inv-zeile" title={r.aktivitaet.label}>{r.aktivitaet.label}</span>
                        {r.aktivitaet.on && <small>{date(r.aktivitaet.on)}</small>}
                      </span>
                    </td>
                  )}
                  <td className={`tk-c-faellig${overdue ? " tk-ueberfaellig" : ""}`} data-label={t("Fällig", "Due")}>{dueDate || due !== "–" ? due : "–"}</td>
                  <td className="tk-c-betrag money" data-label={t("Betrag", "Amount")}>
                    <span className="tk-invcell">
                      <Money cents={r.v.row.gross_cents} currency={r.v.row.currency} />
                      {r.balance > 0 && r.balance !== (r.v.row.gross_cents ?? 0) && <small className="tk-alt-saldo">{t("offen", "open")} <Money cents={r.balance} currency={r.v.row.currency} /></small>}
                    </span>
                  </td>
                  <td className="tk-c-schritt" data-label={t("Nächster Schritt", "Next step")}>
                    <span className="row tk-inv-schritt">
                      {/* 0425: in Pause und Nur-Lesen keine Schritte wie „An Marke senden“ oder „Rechnung prüfen“, nur Öffnen */}
                      {nurLesen
                        ? <Link className="btn" to={`/agentur/rechnungen/${id}`} title={nurLesenSatz(nurLesenPause)}>{t("Öffnen", "Open")}</Link>
                        : schritt.knopf
                        ? <Link className="btn primary" to={nextHref(r)} title={schritt.label} aria-label={schritt.label}>{schritt.kurz ?? schritt.label}</Link>
                        : r.v.next ? <Link className="tk-inv-hinweis" to={`/agentur/rechnungen/${id}`} title={schritt.label}>{schritt.label}</Link>
                        : <Link className="btn" to={`/agentur/rechnungen/${id}`} title={schritt.label}>{schritt.label}</Link>}
                      <Mehr>{(close) => (
                        <>
                          <Link role="menuitem" className="tk-inv-menuepunkt" to={`/agentur/rechnungen/${id}`} onClick={close}>{t("Rechnung öffnen", "Open invoice")}</Link>
                          <button type="button" role="menuitem" className="tk-inv-menuepunkt" onClick={() => { close(); setSchnell(r); }}>{t("Schnellansicht", "Quick view")}</button>
                          {!(r.v.row.status === "draft" && !r.v.row.pdf_path) && (
                            <button type="button" role="menuitem" className="tk-inv-menuepunkt" onClick={() => { close(); setVorschau({ title: t(`Vorschau: ${nr}`, `Preview: ${nr}`), laden: () => belegLaden(r) }); }}>{t("Vorschau", "Preview")}</button>
                          )}
                          {/* K-542: Schritte der Detailseite; dort öffnet sich derselbe Dialog mit Rückfrage */}
                          {!nurLesen && r.aktionen.map((x) => (
                            <Link key={x} role="menuitem" className="tk-inv-menuepunkt" to={x === "pruefen" ? `/agentur/rechnungen/${id}` : `/agentur/rechnungen/${id}?${new URLSearchParams({ aktion: x })}`} onClick={close}>{zeilenAktionText(x, r.v.row)}</Link>
                          ))}
                          <button type="button" role="menuitem" className="tk-inv-menuepunkt" onClick={() => { close(); pdfLaden(r); }} disabled={r.v.row.status === "draft" && !r.v.row.pdf_path}>{t("PDF herunterladen", "Download PDF")}</button>
                          {!nurLesen && darfMahnen && r.v.row.kind !== "talent_to_agency" && (r.v.state.state === "overdue" || r.v.state.state === "dunning" || r.v.state.state === "due_today" || r.v.state.state === "due_soon") && !schritt.knopf && (
                            <Link role="menuitem" className="tk-inv-menuepunkt" to={erinnernHref(id)} onClick={close}>{t("Erinnerung oder Mahnung senden", "Send reminder or notice")}</Link>
                          )}
                          <button type="button" role="menuitem" className="tk-inv-menuepunkt" onClick={() => { close(); umschalten(id); }}>{auswahl.has(id) ? t("Aus der Auswahl nehmen", "Remove from selection") : t("Zur Auswahl hinzufügen", "Add to selection")}</button>
                        </>
                      )}</Mehr>
                    </span>
                  </td>
                </tr>
              );
  };
  // K-523: Gruppen mit Zwischensummen (Brutto und offener Saldo je Währung; Gutschriften und Stornos mit Vorzeichen wie in der Liste)
  const gruppen = gruppe === "" ? null : (() => {
    const m = new Map<string, { key: string; name: string; sort: string; ohne: boolean; rows: Row[]; summen: Map<string, { brutto: number; offen: number }> }>();
    for (const r of sorted) {
      const mon = leistungsmonat(r);
      const [key, name, ohne]: [string, string, boolean] = gruppe === "monat" ? [mon || "ohne", mon ? monthLabel(mon) : t("Ohne Monat", "No month"), !mon]
        : gruppe === "creator" ? [r.v.row.talent_id ?? "ohne", r.v.row.talent_id && r.talent !== "–" ? r.talent : t("Ohne Creator", "No creator"), !r.v.row.talent_id]
        : [r.brandId ?? "ohne", r.brandId && r.brand ? r.brand : t("Ohne Marke", "No brand"), !r.brandId];
      const g = m.get(key) ?? { key, name, sort: gruppe === "monat" ? mon : name.toLocaleLowerCase(), ohne, rows: [] as Row[], summen: new Map<string, { brutto: number; offen: number }>() };
      g.rows.push(r);
      // K-563 b: Eingang (Rechnungen der Creator an die Agentur) und Ausgang getrennt summieren, nie vermischt
      const cur = `${r.v.row.kind === "talent_to_agency" ? "ein" : "aus"}|${r.v.row.currency}`;
      const x = g.summen.get(cur) ?? { brutto: 0, offen: 0 };
      x.brutto += r.v.row.gross_cents ?? 0;
      x.offen += r.balance;
      g.summen.set(cur, x);
      m.set(key, g);
    }
    // Monate neueste zuerst, sonst alphabetisch; „Ohne …“ am Ende
    return [...m.values()].sort((a, b) => Number(a.ohne) - Number(b.ohne) || (gruppe === "monat" ? b.sort.localeCompare(a.sort) : a.sort.localeCompare(b.sort, "de")))
      .map((g) => {
        const beide = [...g.summen.keys()].some((k) => k.startsWith("ein|")) && [...g.summen.keys()].some((k) => k.startsWith("aus|"));
        return { ...g, summen: [...g.summen.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([k, x]) => {
          const [richtung, cur] = k.split("|") as ["ein" | "aus", string];
          return [cur, x, beide ? (richtung === "ein" ? t("Eingang", "Incoming") : t("Ausgang", "Outgoing")) : null] as const;
        }) };
      });
  })();
  return (
    <main id="main" className="content tk-rechnungen">
      <PageHead title={t("Rechnungen", "Invoices")} text={t("Prüfung, Versand und Zahlung bleiben getrennte Schritte.", "Review, sending and payment remain separate steps.")}>
        <div className="row">
          {/* K-470: Hauptaktion zuerst, auch in der Tab- und Vorlesereihenfolge */}
          <Link className="btn primary" to="/agentur/rechnungen/neu" data-coco="rechnung-erstellen">{t("Rechnung erstellen", "Create invoice")}</Link>
          <PdfDesignLink art="invoice" />
          {/* D-2 (Inhaber 21.10.2026): eine Hauptaktion, alle Nebenaktionen hinter „Mehr“ */}
          <KopfMehr>
            {!nurLesen && ws.can("confirm_payments") && ws.can("view_amounts") && (ws.isChef || ws.membership.sees_all_talents) && <Link to="/agentur/rechnungen/kontoauszug">{t("Kontoauszug abgleichen", "Reconcile bank statement")}</Link>}
            {ws.can("view_amounts") && <Link to="/agentur/kennzahlen/liquiditaet">{t("Liquidität", "Liquidity")}</Link>}
            {!nurLesen && <Link to="/agentur/rechnungen/import">{t("Bestand übernehmen", "Take over existing data")}</Link>}
            <VoreinstellungenEintrag modul="rechnungen" />
          </KopfMehr>
        </div>
      </PageHead>
      <Toolbar
        search={search}
        onSearch={setSearch}
        label={t("Rechnungen suchen", "Search invoices")}
        placeholder={t("Rechnung, Creator oder Marke …", "Invoice, creator or brand …")}
        filters={FILTERS.filter((f) => f.key !== "pruefung" || counts.pruefung > 0 || filter === "pruefung").map((f) => ({ ...f, count: counts[f.key] }))}
        filter={filter}
        onFilter={setFilter}
      >
        <select aria-label={t("Leistungsmonat", "Service month")} value={monatFilter} onChange={(e) => setMonatFilter(e.target.value)}>
          <option value="">{t("Alle Monate", "All months")}</option>
          {monate.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
        </select>
        <select aria-label={t("Gruppieren", "Group")} value={gruppe} onChange={(e) => { setGruppe(e.target.value as typeof gruppe); setZugeklappt(new Set()); }}>
          <option value="">{t("Nicht gruppieren", "No grouping")}</option>
          <option value="monat">{t("Nach Leistungsmonat", "By service month")}</option>
          <option value="creator">{t("Nach Creator", "By creator")}</option>
          <option value="marke">{t("Nach Marke", "By brand")}</option>
        </select>
        {/* Handy: Ansicht als Auswahlfeld statt acht Knöpfen (K-409) */}
        <select className="tk-inv-filterwahl" aria-label={t("Ansicht", "View")} value={filter} onChange={(e) => setFilter(e.target.value as ListFilter)}>
          {FILTERS.filter((f) => f.key !== "pruefung" || counts.pruefung > 0 || filter === "pruefung").map((f) => <option key={f.key} value={f.key}>{f.label} ({counts[f.key]})</option>)}
        </select>
        {/* Kompakt (D-1): Ausfuhr als kleines Menü; Kennzahlen in der zweiten Zeile der Werkzeugleiste (am Handy wie bisher darunter) */}
        <KopfMehr label={t("Export", "Export")}>
          <button type="button" onClick={() => exportCsv(sorted)} disabled={!sorted.length}>{t("Als CSV", "As CSV")}</button>
          <button type="button" onClick={() => exportZip(sorted)} disabled={!sorted.length || !!busy} aria-busy={busy === "zip"}>{busy === "zip" ? t("ZIP wird erstellt …", "Creating ZIP …") : t("PDFs als ZIP", "PDFs as ZIP")}</button>
        </KopfMehr>
        {/* R-17 (Umbauten 03.11.2026): vier Kacheln mit Klickziel statt Zahlenwand; weitere Währungen eingeklappt */}
        {sichtbar && kennzahlen.length > 0 && (
          <RechnungsKennzahlen kennzahlen={kennzahlen} filter={filter} nurForderungen={nurForderungen}
            onFilter={(f, forderungen) => { setFilterState(f); setNurForderungen(forderungen); }} />
        )}
      </Toolbar>
      {antworten.length > 0 && (
        <div className="notice warning row between tk-inv-antwort" role="status">
          <span>
            <strong>{antworten.length === 1 ? t("Ein Creator hat geantwortet", "A creator has replied") : t(`${antworten.length} Antworten von Creatorn`, `${antworten.length} replies from creators`)}</strong>
            <br />
            <small>{antworten.slice(0, 2).map((r) => `${r.v.row.number ?? t("ohne Nummer", "no number")}: ${r.status.text}`).join(" · ")}{antworten.length > 2 ? " …" : ""}</small>
          </span>
          {antworten.length === 1
            ? <Link className="btn" to={`/agentur/rechnungen/${antworten[0]!.v.row.id}`}>{t("Anzeigen", "Show")}</Link>
            : <button type="button" className="btn" onClick={() => setFilter("handlung")}>{t("Anzeigen", "Show")}</button>}
        </div>
      )}
      {nurForderungen && filter === "ueberfaellig" && (
        <p className="notice row between" role="status">
          <span>{t(`Nur überfällige Forderungen an Marken und Creator (${shown.length}), wie in den Kennzahlen.`, `Only overdue receivables from brands and creators (${shown.length}), as in the metrics.`)}</span>
          <button type="button" className="tk-linkbtn" onClick={() => setNurForderungen(false)}>{t(`Alle Überfälligen zeigen (${counts.ueberfaellig})`, `Show all overdue (${counts.ueberfaellig})`)}</button>
        </p>
      )}
      {auswahl.size > 0 && (
        <div className="row tk-inv-auswahl" role="region" aria-label={t("Auswahl", "Selection")}>
          <strong>{t(`${gewaehlt.length} ausgewählt`, `${gewaehlt.length} selected`)}</strong>
          <button type="button" onClick={() => exportCsv(gewaehlt)} disabled={!gewaehlt.length}>{t("Als CSV", "As CSV")}</button>
          <button type="button" onClick={() => exportZip(gewaehlt)} disabled={!gewaehlt.length || !!busy}>{busy === "zip" ? t("ZIP wird erstellt …", "Creating ZIP …") : t("PDFs als ZIP", "PDFs as ZIP")}</button>
          {darfMahnen && <button type="button" className="primary" onClick={alleErinnern} disabled={!faelligeAuswahl.length}
            title={t("Öffnet das Versandfenster für jede fällige Rechnung nacheinander; jede wird einzeln bestätigt.", "Opens the send window for each due invoice in turn; each one is confirmed individually.")}>
            {t(`Erinnerung an alle fälligen (${faelligeAuswahl.length})`, `Remind all due (${faelligeAuswahl.length})`)}</button>}
          <button type="button" className="tk-linkbtn" onClick={() => setAuswahl(new Set())}>{t("Auswahl aufheben", "Clear selection")}</button>
        </div>
      )}
      <SortWahl />
      {/* K-573: ohne jede Rechnung erklärt Coco im Rundgang diese leere Liste statt auf „Rechnung erstellen“ zu zeigen */}
      <div ref={tabelle} className="tablewrap tk-invlist tk-sticky-end" data-ohne-akt={aktSpalte ? undefined : ""} data-coco-leer={rows.length === 0 ? "rechnung-erstellen" : undefined}>
        <table>
          <caption className="sr-only">{t("Rechnungsübersicht", "Invoice overview")}</caption>
          <thead>
            <tr>
              <th scope="col" className="tk-c-sel"><input type="checkbox" checked={alleGewaehlt} onChange={(e) => setAlle(e.target.checked)} disabled={!sorted.length} aria-label={t("Alle Rechnungen dieser Ansicht auswählen", "Select all invoices in this view")} /></th>
              <SortTh label={t("Rechnung / Datum", "Invoice / date")} k="nummer" className="tk-c-nr" sort={sort} toggle={toggle} />
              <SortTh label={t("Kunde / Betreff", "Customer / subject")} k="kunde" className="tk-c-kunde" sort={sort} toggle={toggle} />
              {!ws.isCreator && <SortTh label="Creator" k="talent" className="tk-c-creator" sort={sort} toggle={toggle} />}
              <SortTh label="Status" k="status" className="tk-c-status" sort={sort} toggle={toggle} />
              {aktSpalte && <SortTh label={t("Letzte Aktivität", "Last activity")} k="aktivitaet" className="tk-c-akt" sort={sort} toggle={toggle} />}
              <SortTh label={t("Fällig", "Due")} k="faellig" className="tk-c-faellig" sort={sort} toggle={toggle} />
              <SortTh label={t("Betrag", "Amount")} k="betrag" className="tk-c-betrag" sort={sort} toggle={toggle} money />
              <SortTh label={t("Nächster Schritt", "Next step")} k="schritt" className="tk-c-schritt" sort={sort} toggle={toggle} />
            </tr>
          </thead>
          <tbody>
            {gruppen === null ? seite.sichtbar.map(zeileVon) : gruppen.map((g) => {
              const zu = zugeklappt.has(g.key);
              // seitenweise auch in Gruppen: die Portion läuft über die Gruppen in ihrer Reihenfolge, die Zwischensummen bleiben ganz
              const vorher = gruppen.slice(0, gruppen.indexOf(g)).reduce((n, x) => n + x.rows.length, 0);
              const zeilen = g.rows.slice(0, Math.max(0, seite.anzahl - vorher));
              if (!zeilen.length) return null;
              return (
                <Fragment key={g.key}>
                  <tr className="tk-inv-gruppe">
                    <th colSpan={aktSpalte ? 9 : 8} scope="rowgroup">
                      <button type="button" aria-expanded={!zu} onClick={() => setZugeklappt((x) => { const n = new Set(x); if (n.has(g.key)) n.delete(g.key); else n.add(g.key); return n; })}>
                        <span aria-hidden="true">{zu ? "▸" : "▾"}</span> {g.name}
                        <small>{[g.rows.length === 1 ? t("1 Rechnung", "1 invoice") : t(`${g.rows.length} Rechnungen`, `${g.rows.length} invoices`),
                          ...g.summen.map(([cur, x, richtung]) => (richtung ? `${richtung} ` : "") + t(`${money(x.brutto, cur)} brutto`, `${money(x.brutto, cur)} gross`) + (x.offen > 0 ? t(` · ${money(x.offen, cur)} offen`, ` · ${money(x.offen, cur)} open`) : ""))].join(" · ")}</small>
                      </button>
                    </th>
                  </tr>
                  {!zu && zeilen.map(zeileVon)}
                </Fragment>
              );
            })}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={aktSpalte ? 9 : 8}>{search ? t("Keine passenden Ergebnisse. Passe die Suche oder die Filter an.", "No matching results. Adjust the search or the filters.") : t("In dieser Ansicht gibt es keine Rechnungen.", "There are no invoices in this view.")}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <MehrLaden rest={seite.rest} groesse={seite.groesse} onMehr={seite.mehr} onAlle={seite.alle} einheit={t("Rechnungen", "invoices")} />
      <PdfVorschau quelle={vorschau} onClose={() => setVorschau(null)} />
      {schnell && (
        <SchnellAnsicht r={schnell} orgId={orgId} nextHref={nextHref(schnell)} onClose={() => setSchnell(null)}
          onVorschau={!(schnell.v.row.status === "draft" && !schnell.v.row.pdf_path) ? () => { const r = schnell; setSchnell(null); setVorschau({ title: t(`Vorschau: ${r.v.row.number ?? ""}`, `Preview: ${r.v.row.number ?? ""}`), laden: () => belegLaden(r) }); } : null} />
      )}
      <p className="source-note">
        {sorted.length === 1 ? t("1 Rechnung", "1 invoice") : t(`${sorted.length} Rechnungen`, `${sorted.length} invoices`)} {ta("· „Als CSV“ und „PDFs als ZIP“ nehmen genau diese Liste (Filter und Suche) bzw. die Auswahl; im ZIP liegen die PDFs in Ordnern Leistungsmonat/Marke/Creator. Ein- und Ausgangsrechnungen stehen gemeinsam in der Liste; eingehende tragen die Agentur als Kunden. Saldo ist der noch offene Bruttobetrag nach Teilzahlungen; Gutschriften stehen bei den Eingangsrechnungen, Stornorechnungen bei den stornierten; „übernommen“ heißt aus dem bisherigen System.", "· “As CSV” and “PDFs as ZIP” take exactly this list (filters and search) or the selection; in the ZIP the PDFs are in folders service month/brand/creator. Incoming and outgoing invoices are listed together; incoming ones show the agency as customer. Balance is the still open gross amount after partial payments; self-billing invoices are listed with the incoming invoices, cancellation invoices with the cancelled ones; “taken over” means from the previous system.", "· „Als CSV“ und „PDFs als ZIP“ nehmen genau diese Liste (Filter und Suche) bzw. die Auswahl; im ZIP liegen die PDFs in Ordnern Leistungsmonat/Marke/Creator. Saldo ist der noch offene Bruttobetrag nach Teilzahlungen; Stornorechnungen stehen bei den stornierten; „übernommen“ heißt aus dem bisherigen System.", "· “As CSV” and “PDFs as ZIP” take exactly this list (filters and search) or the selection; in the ZIP the PDFs are in folders service month/brand/creator. Balance is the still open gross amount after partial payments; cancellation invoices are listed with the cancelled ones; “taken over” means from the previous system.")}</p>
    </main>
  );
}

/**
 * K-518: Schnellansicht aus der Liste: Kopf (Kunde, Betreff, Creator), Stand, Betrag, Saldo, Fälligkeit, der nächste Schritt und
 * die Aktionen des Zeilenmenüs, die letzten Ereignisse und die Belegvorschau. Was dort geändert wird, passiert auf der
 * Detailseite (dieselben Dialoge mit Rückfrage).
 */
function SchnellAnsicht({ r, orgId, nextHref, onClose, onVorschau }: { r: Row; orgId: string; nextHref: string; onClose: () => void; onVorschau: (() => void) | null }) {
  const nurLesen = useNurLesen();
  const inv = r.v.row;
  const ereignisse = useQuery({
    queryKey: ["invoice_events", inv.id, "schnell"],
    queryFn: () => api.select<InvoiceEventRow>("invoice_events", { columns: "id, event, actor, at, details", filters: [["invoice_id", "eq", inv.id], ["org_id", "eq", orgId]], order: [["at", "desc"]], limit: 8 }),
  });
  const schritt = naechsterSchritt(r.v, kurzDatum);
  const nr = inv.number ?? t("ohne Nummer", "no number");
  return (
    <Dialog open onClose={onClose} title={t(`Rechnung ${nr}`, `Invoice ${nr}`)} wide>
      <div className="stack tk-inv-schnell">
        <div className="tk-inv-schnell-kopf">
          <div className="stack" style={{ gap: 2 }}>
            <strong>{r.kunde}</strong>
            {r.betreff && <span className="muted">{r.betreff}</span>}
            {r.talent !== "–" && r.talent !== r.kunde && <span className="muted">{t(`Creator: ${r.talent}`, `Creator: ${r.talent}`)}</span>}
            {r.v.row.kind === "talent_to_agency" && <span className="muted">{t(`Rechnung an ${r.customer}`, `Invoice to ${r.customer}`)}</span>}
          </div>
          <span className={`tk-inv-status tk-ton-${r.status.tone}`}><span className="tk-inv-punkt" aria-hidden="true" />{r.status.text}</span>
        </div>
        <dl className="tk-inv-schnell-fakten">
          <div><dt>{t("Betrag", "Amount")}</dt><dd className="num"><Money cents={inv.gross_cents} currency={inv.currency} /></dd></div>
          <div><dt>{t("Offen", "Open")}</dt><dd className="num"><Money cents={r.balance} currency={inv.currency} /></dd></div>
          <div><dt>{t("Rechnungsdatum", "Invoice date")}</dt><dd>{inv.issue_date ? date(inv.issue_date) : "–"}</dd></div>
          <div><dt>{t("Fällig", "Due")}</dt><dd>{inv.effective_due_date ? date(inv.effective_due_date) : "–"}</dd></div>
          {r.stufe && <div><dt>{t("Mahnstufe", "Dunning level")}</dt><dd><span className={`pill ${r.stufe.tone}`}>{r.stufe.label}</span></dd></div>}
          <div><dt>{t("Am Zug", "Up next")}</dt><dd>{r.responsible}</dd></div>
        </dl>
        {r.status.sub && <p className="muted">{r.status.sub}</p>}
        <div className="row tk-inv-schnell-knoepfe">
          {schritt.knopf && !nurLesen && <Link className="btn primary" to={nextHref} onClick={onClose}>{schritt.label}</Link>}
          {r.aktionen.map((x) => (
            !nurLesen && <Link key={x} className="btn" onClick={onClose} to={x === "pruefen" ? `/agentur/rechnungen/${inv.id}` : `/agentur/rechnungen/${inv.id}?${new URLSearchParams({ aktion: x })}`}>{zeilenAktionText(x, inv)}</Link>
          ))}
          {onVorschau && <button type="button" onClick={onVorschau}>{t("Vorschau", "Preview")}</button>}
          <Link className="btn" to={`/agentur/rechnungen/${inv.id}`} onClick={onClose}>{t("Rechnung öffnen", "Open invoice")}</Link>
        </div>
        <section className="stack" aria-labelledby="schnell-verlauf">
          <h3 id="schnell-verlauf">{t("Letzte Schritte", "Recent steps")}</h3>
          {ereignisse.isPending ? <p className="muted">{t("Wird geladen …", "Loading …")}</p> : (() => {
            // ohne Verlaufseinträge (übernommene oder ältere Rechnungen): Meilensteine aus der Rechnung selbst
            const zeilen = (ereignisse.data ?? []).length
              ? (ereignisse.data ?? []).map((e) => ({ k: String(e.id), at: e.at, text: EVENT_LABEL[e.event] ?? e.event }))
              : ([[inv.paid_at, t("Bezahlt", "Paid")], [inv.last_dunning_at, t("Zuletzt gemahnt", "Last reminder")], [inv.sent_at, t("Versendet", "Sent")],
                [inv.approved_at, t("Freigegeben", "Approved")], [inv.submitted_at, t("Eingereicht", "Submitted")], [inv.created_at, t("Angelegt", "Created")]] as [string | null, string][])
                .filter(([at]) => !!at).map(([at, text]) => ({ k: text, at: at!, text }));
            return zeilen.length === 0 ? <p className="muted">{t("Noch keine Einträge.", "No entries yet.")}</p> : (
              <ol className="tk-inv-schnell-verlauf">
                {zeilen.map((e) => <li key={e.k}><span className="muted num">{date(e.at)}</span> {e.text}</li>)}
              </ol>
            );
          })()}
        </section>
      </div>
    </Dialog>
  );
}
